import { Theme } from "@mui/material";
import { createStyles, WithStyles, withStyles } from "@mui/styles";
import { ICellRendererParams } from "ag-grid-community";
import { observer } from "mobx-react";
import * as React from "react";
import { TableChildProps } from "../../coreui/Table";
import { CellUtil } from "../../coreui/table/CellUtil";
import Thumbnail, { ThumbnailTypes } from "../../coreui/Thumbnail";
import PaneRow, { RuntimeWidget } from "../../models/PaneRow";
import DocumentService from "../../services/DocumentService";
import { AccessLevel } from "../AccessLevel";
import { FunctionName } from "../TableSummary";
import { GridColumnConfigProperties } from "./GridColumn";

interface ConfigProperties extends ICellRendererParams<PaneRow, null> {
  dataId: string;
  imageHeight: number;
  imageWidth: number;
  name: string;
  thumbnailType: ThumbnailTypes;
}

interface RuntimeProperties {
  accessLevel: AccessLevel;
  alternateText: string;
  documentHandle: string | null;
  downloadToken: string;
  fileName: string;
  hasThumbnailImage: boolean;
  iconName: string;
  pendingDocumentId: number | null;
  pendingThumbnailId: number | null;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      alignItems: "center",
      display: "flex",
      height: "100%",
      justifyContent: "center",
      marginLeft: -1,
      width: "100%",
    },
  });

export class DataImageDisplayColumn extends React.PureComponent<
  ConfigProperties & WithStyles<typeof styles>
> {
  public static readonly widgetType: string = "DataImageDisplayColumn";

  private linkRef: HTMLElement | null;

  public static getFilterText(
    column: GridColumnConfigProperties,
    propagated: TableChildProps,
    row: PaneRow
  ): string {
    return "";
  }

  public static getSummaryValue(
    runtimeData: RuntimeWidget[],
    configProperties: ConfigProperties,
    functionName: FunctionName
  ): string | null {
    let result = 0;
    for (const data of runtimeData) {
      const runtimeProperties = data.properties as RuntimeProperties;

      if (
        runtimeProperties.accessLevel >= AccessLevel.readOnly &&
        (runtimeProperties.documentHandle ||
          runtimeProperties.pendingDocumentId)
      ) {
        result += 1;
      }
    }

    return result.toString();
  }

  public constructor(props: ConfigProperties & WithStyles<typeof styles>) {
    super(props);

    CellUtil.setReadOnlyAttribute(props.eGridCell, true);
    props.eGridCell.addEventListener("keydown", this.onCellKeyDown);
    props.eGridCell.addEventListener("focus", this.onCellFocus);
  }

  private onCellFocus = (): void => {
    if (this.linkRef) {
      this.linkRef.focus();
    }
  };

  private onCellKeyDown = (event: KeyboardEvent): void => {
    CellUtil.customizeGridNavigation(event, this.props);
  };

  public componentWillUnmount(): void {
    this.props.eGridCell.removeEventListener("focus", this.onCellFocus);
    this.props.eGridCell.removeEventListener("keydown", this.onCellKeyDown);
  }

  public render(): React.ReactNode {
    const row = this.props.data!;
    const widget = row.getWidgetT<null, RuntimeProperties>(this.props.name);

    if (widget.properties.accessLevel === AccessLevel.hidden) {
      return null;
    }

    if (
      !widget.properties.documentHandle &&
      !widget.properties.pendingDocumentId
    ) {
      return null;
    }

    const imgSrc: string = DocumentService.getThumbnailUrl(
      widget.properties.documentHandle,
      widget.properties.pendingDocumentId,
      widget.properties.pendingThumbnailId,
      this.props.thumbnailType,
      widget.properties.downloadToken
    );

    const downloadUrl: string = DocumentService.getDocumentUrl(
      widget.properties.documentHandle,
      widget.properties.pendingDocumentId,
      widget.properties.fileName,
      widget.properties.downloadToken
    );

    return (
      <div className={this.props.classes.root}>
        <Thumbnail
          alternateText={widget.properties.alternateText}
          disableFocusRipple={true}
          downloadUrl={downloadUrl}
          hasImage={widget.properties.hasThumbnailImage}
          iconName={widget.properties.iconName}
          imageHeight={this.props.imageHeight}
          imageWidth={this.props.imageWidth}
          imgSrc={imgSrc}
          inGrid={true}
          linkRef={(r) => (this.linkRef = r)}
          tabIndex={-1}
          thumbnailType={this.props.thumbnailType}
        />
      </div>
    );
  }
}

export default withStyles(styles)(observer(DataImageDisplayColumn));
